import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  hasPaymentMetadataOrders,
  hasPrePayMethod,
  PaymentMetadata,
  PrePayExtraMethods,
} from '@ca/common'
import { trpc } from 'api'
import { useToast } from '@ui/toasts/useToastManager'
import usePayments, { getOrderSetKey } from '@order/hooks/usePayments'
import { useCurrencyDecimals } from 'hooks/useCurrency'
import {
  resolveUncertainAttempt,
  settleUncertainAttempt,
} from './globemedAttempt'

type Options = {
  orderIds: number[]
  localId: string
  // Metadata stored on the payment (refund balance check or GlobeMed)
  metadata: PaymentMetadata | null
}

// GlobeMed state for a payment's orders, and when ERP has confirmed it
export default function useGlobemedPayment({
  orderIds,
  localId,
  metadata,
}: Options) {
  const { t } = useTranslation(['payment'])
  const toast = useToast()
  const decimals = useCurrencyDecimals()

  // Kept out of the store so it does not switch the screen into
  // refund-balance mode; it drives GlobeMed and the custom amount range
  const metadataQuery = trpc.order.payments.metadata.useQuery(
    { order_ids: orderIds },
    { enabled: orderIds.length > 0 }
  )
  const freshMetadata =
    metadataQuery.isFetchedAfterMount &&
    !metadataQuery.isFetching &&
    hasPaymentMetadataOrders(metadataQuery.data)
      ? metadataQuery.data
      : null

  const attempt = usePayments(
    (s) => s.globemedAttempts[getOrderSetKey(orderIds)] ?? null
  )
  const setMetadata = usePayments((s) => s.setMetadata)
  const setGlobemedAttempt = usePayments((s) => s.setGlobemedAttempt)
  const clearGlobemedAttempt = usePayments((s) => s.clearGlobemedAttempt)
  const markOrdersSubmitted = usePayments((s) => s.markOrdersSubmitted)

  // Only metadata fetched after the GlobeMed request reflects it
  const isFetchedAfterAttempt =
    attempt !== null &&
    metadataQuery.isFetchedAfterMount &&
    !metadataQuery.isFetching &&
    !metadataQuery.isError &&
    metadataQuery.dataUpdatedAt >= attempt.createdAt
  // Any valid ERP metadata after the attempt is the balance to pay against
  const confirmedMetadata = isFetchedAfterAttempt ? freshMetadata : null
  // Pay stays blocked until ERP's balance for the GlobeMed attempt is known
  const isPending = attempt !== null && confirmedMetadata === null
  // Loading keeps the normal Pay button; Refresh only replaces it after a failure
  const hasResultAfterAttempt =
    attempt !== null &&
    (metadataQuery.isError
      ? metadataQuery.errorUpdatedAt >= attempt.createdAt
      : metadataQuery.dataUpdatedAt >= attempt.createdAt)
  const isFailed =
    isPending && (hasResultAfterAttempt || !metadataQuery.isFetching)

  // Keep the payment on ERP's post-GlobeMed metadata, also for new drafts
  useEffect(() => {
    if (!confirmedMetadata) {
      return
    }
    if (
      metadata?.prepaid_amount === confirmedMetadata.prepaid_amount &&
      metadata?.rest_amount === confirmedMetadata.rest_amount
    ) {
      return
    }
    setMetadata(localId, confirmedMetadata)
  }, [confirmedMetadata, metadata, localId, setMetadata])

  // Settle an uncertain request from metadata; never resend it
  useEffect(() => {
    if (attempt?.status !== 'uncertain' || !isFetchedAfterAttempt) {
      return
    }
    const resolution = resolveUncertainAttempt(
      attempt,
      metadataQuery.data,
      decimals
    )
    const settled = settleUncertainAttempt(attempt, resolution)
    if (settled.attempt) {
      setGlobemedAttempt(orderIds, settled.attempt)
    } else {
      clearGlobemedAttempt(orderIds)
    }
    if (settled.attempt?.status === 'applied') {
      markOrdersSubmitted(orderIds)
    }
    // Unclear results keep paying on ERP's balance, just without a GlobeMed row
    if (settled.notice) {
      toast({
        variant: settled.notice === 'unclear' ? 'warning' : 'info',
        text: t(`payment:globemed.pending.${settled.notice}`),
      })
    }
  }, [
    attempt,
    isFetchedAfterAttempt,
    metadataQuery.data,
    decimals,
    orderIds,
    setGlobemedAttempt,
    clearGlobemedAttempt,
    markOrdersSubmitted,
    toast,
    t,
  ])

  const refreshBalance = async () => {
    const result = await metadataQuery.refetch()
    if (result.isError) {
      toast({ variant: 'error', text: t('payment:globemed.pending.error') })
    }
  }

  const isAvailable = hasPrePayMethod(
    metadataQuery.data?.prepay_method,
    PrePayExtraMethods.globemed
  )

  return {
    metadataQuery,
    freshMetadata,
    // Shown as its own row only once applied
    amount: attempt?.status === 'applied' ? attempt.amount : null,
    // A change ERP has not confirmed would otherwise show as refund balance
    hasUnresolvedChange:
      attempt?.status === 'uncertain' || attempt?.status === 'unclear',
    isPending,
    isFailed,
    refreshBalance,
    // A removed GlobeMed can be added again, updating the same ERP payment
    canAdd: isAvailable && (attempt === null || attempt.status === 'removed'),
    draftPaymentId: attempt?.status === 'removed' ? attempt.paymentId : null,
    // Only a confirmed apply with an ERP payment ID can be removed in the app
    removable:
      attempt?.status === 'applied' && attempt.paymentId !== null
        ? { amount: attempt.amount, paymentId: attempt.paymentId }
        : null,
  }
}
